import { Injectable, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { SearchOptions } from '../models/e621.models';
import { SearchDateService } from './search-date.service';

/**
 * Service responsible for URL query-string persistence and search-option
 * query building.  Extracted from SearchComponent to reduce component complexity.
 */
@Injectable({
  providedIn: 'root',
})
export class SearchUrlService {
  private searchDateService = inject(SearchDateService);

  /** Check whether a score value is valid for inclusion in a query or URL param. */
  private isValidScore(value: number | null | undefined): value is number {
    return value !== null && value !== undefined
        && String(value) !== '' && !isNaN(Number(value));
  }

  // ─── Search Options Query Builder ──────────────────────────────────────────

  /**
   * Build a query-string fragment from search options (date, order, score)
   * that can be appended to a base search query.
   * @param query The current user query (used to detect manual tags).
   * @param options The current search options.
   */
  buildSearchOptionsQuery(query: string, options: SearchOptions): string {
    let optionsQuery = '';

    // Apply advanced date filter (only when no manual date tag in query)
    if (!this.searchDateService.hasManualDateTag(query)) {
      const dateTag = this.searchDateService.buildAdvancedDateTag(options);
      if (dateTag) {
        optionsQuery += ` ${dateTag}`;
      }
    }

    // Apply order (only when no manual order tag in query)
    if (!this.searchDateService.hasManualOrderTag(query) && options.orderBy) {
      optionsQuery += ` order:${options.orderBy}`;
    }

    // Apply score filters (only when no manual score tag in query)
    if (!this.searchDateService.hasManualScoreTag(query)) {
      if (this.isValidScore(options.minScore)) {
        optionsQuery += ` score:>=${options.minScore}`;
      }
      if (this.isValidScore(options.maxScore)) {
        optionsQuery += ` score:<=${options.maxScore}`;
      }
    }

    return optionsQuery.trim();
  }

  /**
   * Build the final search query by appending search options to the base query.
   */
  buildFinalSearchQuery(baseQuery: string, query: string, options: SearchOptions): string {
    const searchOptionsQuery = this.buildSearchOptionsQuery(query, options);
    if (searchOptionsQuery) {
      return `${baseQuery} ${searchOptionsQuery}`.trim();
    }
    return baseQuery;
  }

  // ─── URL Query Params ──────────────────────────────────────────────────────

  /**
   * Build URL query parameters from the current search tags and options.
   */
  buildUrlQueryParams(tags: string, options: SearchOptions): Record<string, string | null> {
    const queryParams: Record<string, string | null> = { tags };

    const mode = options.advancedDateMode || 'none';

    // Persist advanced date controls
    queryParams['dateMode'] = mode !== 'none' ? mode : null;
    queryParams['periodUnit'] = mode === 'period' ? (options.periodUnit || null) : null;
    queryParams['periodOffset'] = mode === 'period' && options.periodOffset != null ? String(options.periodOffset) : null;
    queryParams['exactGranularity'] = mode === 'exact' ? (options.exactGranularity || null) : null;
    queryParams['exactYear'] = mode === 'exact' ? String(options.exactYear) : null;
    queryParams['exactMonth'] = mode === 'exact' && options.exactGranularity !== 'year' ? String(options.exactMonth) : null;
    queryParams['exactDay'] = mode === 'exact' && options.exactGranularity === 'day' ? String(options.exactDay) : null;
    queryParams['rangeGranularity'] = mode === 'range' ? (options.rangeGranularity || null) : null;
    queryParams['rangeFromYear'] = mode === 'range' ? String(options.rangeFromYear) : null;
    queryParams['rangeFromMonth'] = mode === 'range' && options.rangeGranularity !== 'year' ? String(options.rangeFromMonth) : null;
    queryParams['rangeFromDay'] = mode === 'range' && options.rangeGranularity === 'day' ? String(options.rangeFromDay) : null;
    queryParams['rangeToYear'] = mode === 'range' ? String(options.rangeToYear) : null;
    queryParams['rangeToMonth'] = mode === 'range' && options.rangeGranularity !== 'year' ? String(options.rangeToMonth) : null;
    queryParams['rangeToDay'] = mode === 'range' && options.rangeGranularity === 'day' ? String(options.rangeToDay) : null;

    // Persist order & score options
    queryParams['orderBy'] = options.orderBy || null;
    queryParams['minScore'] = this.isValidScore(options.minScore) ? String(options.minScore) : null;
    queryParams['maxScore'] = this.isValidScore(options.maxScore) ? String(options.maxScore) : null;

    return queryParams;
  }

  /**
   * Update the browser URL with the given tags and search options.
   */
  updateUrl(router: Router, route: ActivatedRoute, tags: string, options: SearchOptions): void {
    try {
      const queryParams = this.buildUrlQueryParams(tags, options);
      router.navigate([], {
        relativeTo: route,
        queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
      console.log('URL updated with tags parameter:', tags);
    } catch (error) {
      console.error('Error updating URL with tags parameter:', error);
    }
  }

  // ─── URL Parsing ───────────────────────────────────────────────────────────

  /**
   * Parse URL parameters and return the extracted search state.
   * Returns null if no relevant parameters were found.
   */
  parseUrlParameters(): { tags?: string; searchOptions?: Partial<SearchOptions>; filterGroupId?: string } | null {
    console.log('Checking for URL parameters...');
    console.log('Current URL:', window.location.href);

    let searchString = window.location.search;
    if (!searchString && window.location.pathname.includes('=')) {
      searchString = '?' + window.location.pathname.replace(/^\//, '');
      console.log('Detected query-style parameters in pathname, treating as query string:', searchString);
    }
    const urlParams = new URLSearchParams(searchString);
    const tagsParam = urlParams.get('tags');

    console.log('URLSearchParams result:', tagsParam);
    console.log('All URL params:', Object.fromEntries(urlParams.entries()));

    let foundAny = false;
    const result: { tags?: string; searchOptions?: Partial<SearchOptions>; filterGroupId?: string } = {};

    if (tagsParam && tagsParam.trim()) {
      console.log('Loading search query from URL parameter:', tagsParam);
      result.tags = tagsParam.trim();
      foundAny = true;
    }

    // Restore advanced date controls from URL
    const dateMode = urlParams.get('dateMode');
    if (dateMode) {
      const patch: Partial<SearchOptions> = { advancedDateMode: dateMode };
      if (dateMode === 'period') {
        if (urlParams.has('periodUnit'))   patch.periodUnit   = urlParams.get('periodUnit')!;
        if (urlParams.has('periodOffset')) patch.periodOffset  = Number(urlParams.get('periodOffset'));
      } else if (dateMode === 'exact') {
        if (urlParams.has('exactGranularity')) patch.exactGranularity = urlParams.get('exactGranularity')!;
        if (urlParams.has('exactYear'))  patch.exactYear  = Number(urlParams.get('exactYear'));
        if (urlParams.has('exactMonth')) patch.exactMonth = Number(urlParams.get('exactMonth'));
        if (urlParams.has('exactDay'))   patch.exactDay   = Number(urlParams.get('exactDay'));
      } else if (dateMode === 'range') {
        if (urlParams.has('rangeGranularity')) patch.rangeGranularity = urlParams.get('rangeGranularity')!;
        if (urlParams.has('rangeFromYear'))  patch.rangeFromYear  = Number(urlParams.get('rangeFromYear'));
        if (urlParams.has('rangeFromMonth')) patch.rangeFromMonth = Number(urlParams.get('rangeFromMonth'));
        if (urlParams.has('rangeFromDay'))   patch.rangeFromDay   = Number(urlParams.get('rangeFromDay'));
        if (urlParams.has('rangeToYear'))    patch.rangeToYear    = Number(urlParams.get('rangeToYear'));
        if (urlParams.has('rangeToMonth'))   patch.rangeToMonth   = Number(urlParams.get('rangeToMonth'));
        if (urlParams.has('rangeToDay'))     patch.rangeToDay     = Number(urlParams.get('rangeToDay'));
      }
      result.searchOptions = { ...(result.searchOptions || {}), ...patch };
      foundAny = true;
    }

    // Restore order & score options from URL
    const orderBy = urlParams.get('orderBy');
    if (orderBy) {
      result.searchOptions = { ...(result.searchOptions || {}), orderBy };
      foundAny = true;
    }
    const minScore = urlParams.get('minScore');
    if (minScore !== null) {
      result.searchOptions = { ...(result.searchOptions || {}), minScore: Number(minScore) };
      foundAny = true;
    }
    const maxScore = urlParams.get('maxScore');
    if (maxScore !== null) {
      result.searchOptions = { ...(result.searchOptions || {}), maxScore: Number(maxScore) };
      foundAny = true;
    }

    // Detect filter group from URL
    const filterGroupId = urlParams.get('filterGroup');
    if (filterGroupId) {
      result.filterGroupId = filterGroupId;
      foundAny = true;
    }

    if (!foundAny) {
      console.log('No tags parameter found in URL');
      return null;
    }

    return result;
  }

  // ─── Search Options Extraction ─────────────────────────────────────────────

  /**
   * Extract the current search options from raw form values into a
   * well-typed SearchOptions object.
   */
  getCurrentSearchOptions(formValues: Record<string, unknown>): SearchOptions {
    return {
      advancedDateMode: (formValues['advancedDateMode'] as string) || 'none',
      periodUnit: formValues['periodUnit'] as string,
      periodOffset: formValues['periodOffset'] as number,
      exactGranularity: formValues['exactGranularity'] as string,
      exactYear: formValues['exactYear'] as number,
      exactMonth: formValues['exactMonth'] as number,
      exactDay: formValues['exactDay'] as number,
      rangeGranularity: formValues['rangeGranularity'] as string,
      rangeFromYear: formValues['rangeFromYear'] as number,
      rangeFromMonth: formValues['rangeFromMonth'] as number,
      rangeFromDay: formValues['rangeFromDay'] as number,
      rangeToYear: formValues['rangeToYear'] as number,
      rangeToMonth: formValues['rangeToMonth'] as number,
      rangeToDay: formValues['rangeToDay'] as number,
      orderBy: (formValues['orderBy'] as string) || '',
      minScore: (formValues['minScore'] as number | undefined) ?? null,
      maxScore: (formValues['maxScore'] as number | undefined) ?? null,
    };
  }
}
